// Answer Sheet entity
export interface AnswerSheet {
    id: number;
    name: string;
    questionsNumber: number;
    numberOfBubbles: number;       // 3, 4, or 5
    answerKeys: string;            // 'A-E', 'a-e', '1-5'
    colorOfBubbles: string;        // 'Blue', 'Black', 'Red', 'Green'
    paperSize: string;             // 'Letter', 'A4', 'Legal'
    imageBase64?: string;          // Base64 encoded image of the answer sheet
    createdAt: string;
    updatedAt: string;
}

export interface IAnswerSheetsState {
    answerSheets: AnswerSheet[];
    selectedAnswerSheet: AnswerSheet | null;
    selectedId: number;
    loading: boolean;
    error: string;
    success: boolean;
    create: {
        loading: boolean;
        error: string;
        success: boolean;
    };
    update: {
        loading: boolean;
        error: string;
        success: boolean;
    };
    delete: {
        loading: boolean;
        error: string;
        success: boolean;
    };
}

//#region -----------------CRUD Models----------------------------

export interface GetAnswerSheetModel {
    id: number;
}

export interface GetAnswerSheetsModel {
    userId: string;
}

export interface GetAnswerSheetsByAssessmentModel {
    assessmentId: number;
}

export interface CreateAnswerSheetModel {
    name: string;
    questionsNumber: number;
    numberOfBubbles: number;
    answerKeys: string;
    colorOfBubbles: string;
    paperSize: string;
}

export interface UpdateAnswerSheetModel {
    name: string;
    questionsNumber: number;
    numberOfBubbles: number;
    answerKeys: string;
    colorOfBubbles: string;
    paperSize: string;
}

export interface DeleteAnswerSheetModel {
    id: number;
}

//#endregion

//#region -----------------CRUD Types----------------------------

export enum GetAnswerSheetActionsTypes {
    GET_ANSWER_SHEET = 'GET_ANSWER_SHEET',
    GET_ANSWER_SHEET_SUCCESS = 'GET_ANSWER_SHEET_SUCCESS',
    GET_ANSWER_SHEET_ERROR = 'GET_ANSWER_SHEET_ERROR'
}

export enum GetAnswerSheetsActionsTypes {
    GET_ANSWER_SHEETS = 'GET_ANSWER_SHEETS',
    GET_ANSWER_SHEETS_SUCCESS = 'GET_ANSWER_SHEETS_SUCCESS',
    GET_ANSWER_SHEETS_ERROR = 'GET_ANSWER_SHEETS_ERROR'
}

export enum GetAnswerSheetsByAssessmentActionsTypes {
    GET_ANSWER_SHEETS_BY_ASSESSMENT = 'GET_ANSWER_SHEETS_BY_ASSESSMENT',
    GET_ANSWER_SHEETS_BY_ASSESSMENT_SUCCESS = 'GET_ANSWER_SHEETS_BY_ASSESSMENT_SUCCESS',
    GET_ANSWER_SHEETS_BY_ASSESSMENT_ERROR = 'GET_ANSWER_SHEETS_BY_ASSESSMENT_ERROR'
}

export enum CreateAnswerSheetActionsTypes {
    POST_CREATE_ANSWER_SHEET = 'POST_CREATE_ANSWER_SHEET',
    POST_CREATE_ANSWER_SHEET_SUCCESS = 'POST_CREATE_ANSWER_SHEET_SUCCESS',
    POST_CREATE_ANSWER_SHEET_ERROR = 'POST_CREATE_ANSWER_SHEET_ERROR'
}

export enum UpdateAnswerSheetActionsTypes {
    PUT_UPDATE_ANSWER_SHEET = 'PUT_UPDATE_ANSWER_SHEET',
    PUT_UPDATE_ANSWER_SHEET_SUCCESS = 'PUT_UPDATE_ANSWER_SHEET_SUCCESS',
    PUT_UPDATE_ANSWER_SHEET_ERROR = 'PUT_UPDATE_ANSWER_SHEET_ERROR'
}

export enum DeleteAnswerSheetActionsTypes {
    DELETE_ANSWER_SHEET = 'DELETE_ANSWER_SHEET',
    DELETE_ANSWER_SHEET_SUCCESS = 'DELETE_ANSWER_SHEET_SUCCESS',
    DELETE_ANSWER_SHEET_ERROR = 'DELETE_ANSWER_SHEET_ERROR'
}

//#endregion

//#region -----------------CRUD Requests----------------------------

// Get Answer Sheet
export interface GetAnswerSheetRequest {
    type: GetAnswerSheetActionsTypes.GET_ANSWER_SHEET;
    id: number;
}

export interface GetAnswerSheetRequestSucces {
    type: GetAnswerSheetActionsTypes.GET_ANSWER_SHEET_SUCCESS;
    data: IGetAnswerSheetResult;
    loading: boolean;
}

export interface GetAnswerSheetRequestError {
    type: GetAnswerSheetActionsTypes.GET_ANSWER_SHEET_ERROR;
    error: string;
    loading: boolean;
}

// Get Answer Sheets
export interface GetAnswerSheetsRequest {
    type: GetAnswerSheetsActionsTypes.GET_ANSWER_SHEETS;
}

export interface GetAnswerSheetsRequestSucces {
    type: GetAnswerSheetsActionsTypes.GET_ANSWER_SHEETS_SUCCESS;
    data: IGetAnswerSheetsResult;
    loading: boolean;
}

export interface GetAnswerSheetsRequestError {
    type: GetAnswerSheetsActionsTypes.GET_ANSWER_SHEETS_ERROR;
    error: string;
    loading: boolean;
}

// Get Answer Sheets by Assessment ID
export interface GetAnswerSheetsByAssessmentRequest {
    type: GetAnswerSheetsByAssessmentActionsTypes.GET_ANSWER_SHEETS_BY_ASSESSMENT;
    assessmentId: number;
}

export interface GetAnswerSheetsByAssessmentRequestSucces {
    type: GetAnswerSheetsByAssessmentActionsTypes.GET_ANSWER_SHEETS_BY_ASSESSMENT_SUCCESS;
    data: IGetAnswerSheetsResult;
    loading: boolean;
}

export interface GetAnswerSheetsByAssessmentRequestError {
    type: GetAnswerSheetsByAssessmentActionsTypes.GET_ANSWER_SHEETS_BY_ASSESSMENT_ERROR;
    error: string;
    loading: boolean;
}

// Create Answer Sheet
export interface CreateAnswerSheetRequest {
    type: CreateAnswerSheetActionsTypes.POST_CREATE_ANSWER_SHEET;
    model: CreateAnswerSheetModel;
}

export interface CreateAnswerSheetRequestSucces {
    type: CreateAnswerSheetActionsTypes.POST_CREATE_ANSWER_SHEET_SUCCESS;
    data: ICreateAnswerSheetResult;
    loading: boolean;
    success: boolean;
}

export interface CreateAnswerSheetRequestError {
    type: CreateAnswerSheetActionsTypes.POST_CREATE_ANSWER_SHEET_ERROR;
    error: string;
    loading: boolean;
}

// Update Answer Sheet
export interface UpdateAnswerSheetRequest {
    type: UpdateAnswerSheetActionsTypes.PUT_UPDATE_ANSWER_SHEET;
    id: number;
    model: UpdateAnswerSheetModel;
}

export interface UpdateAnswerSheetRequestSucces {
    type: UpdateAnswerSheetActionsTypes.PUT_UPDATE_ANSWER_SHEET_SUCCESS;
    data: IUpdateAnswerSheetResult;
    loading: boolean;
}

export interface UpdateAnswerSheetRequestError {
    type: UpdateAnswerSheetActionsTypes.PUT_UPDATE_ANSWER_SHEET_ERROR;
    error: string;
    loading: boolean;
}

// Delete Answer Sheet
export interface DeleteAnswerSheetRequest {
    type: DeleteAnswerSheetActionsTypes.DELETE_ANSWER_SHEET;
    id: number;
}

export interface DeleteAnswerSheetRequestSucces {
    type: DeleteAnswerSheetActionsTypes.DELETE_ANSWER_SHEET_SUCCESS;
    data: IDeleteAnswerSheetResult;
    loading: boolean;
}

export interface DeleteAnswerSheetRequestError {
    type: DeleteAnswerSheetActionsTypes.DELETE_ANSWER_SHEET_ERROR;
    error: string;
    loading: boolean;
}

//#endregion

//#region -----------------CRUD Results----------------------------

export interface IGetAnswerSheetResult {
    data: AnswerSheet;
    success: boolean;
    error: number;
}

export interface IGetAnswerSheetsResult {
    data: {
        answerSheets: AnswerSheet[];
    };
    success: boolean;
    error: number;
}

export interface ICreateAnswerSheetResult {
    data: AnswerSheet;
    success: boolean;
    error: number;
}

export interface IUpdateAnswerSheetResult {
    data: AnswerSheet;
    success: boolean;
    error: number;
}

export interface IDeleteAnswerSheetResult {
    data: null;
    success: boolean;
    error: number;
}

//#endregion

export type AnswerSheetActions = 
    GetAnswerSheetRequest | GetAnswerSheetRequestSucces | GetAnswerSheetRequestError |
    GetAnswerSheetsRequest | GetAnswerSheetsRequestSucces | GetAnswerSheetsRequestError |
    GetAnswerSheetsByAssessmentRequest | GetAnswerSheetsByAssessmentRequestSucces | GetAnswerSheetsByAssessmentRequestError |
    CreateAnswerSheetRequest | CreateAnswerSheetRequestSucces | CreateAnswerSheetRequestError |
    UpdateAnswerSheetRequest | UpdateAnswerSheetRequestSucces | UpdateAnswerSheetRequestError |
    DeleteAnswerSheetRequest | DeleteAnswerSheetRequestSucces | DeleteAnswerSheetRequestError;
